import { useQuery } from '@tanstack/react-query'
import { apiClient } from '../../../../shared/api/client'
import type { DashboardPeriodState } from '../useDashboardPeriod'
import type {
  ClientDetailDto,
  ManagerDetailDto,
  ManagersMonthlyDto,
  PartPriceHistoryDto,
  ProcurementDetailDto,
} from './drilldown.types'

function periodParams(p: DashboardPeriodState): Record<string, string> {
  const params: Record<string, string> = { period: p.key }
  if (p.key === 'custom' && p.from && p.to) {
    params.from = p.from
    params.to = p.to
  }
  return params
}

export function useManagerDetail(managerId: string | null, period: DashboardPeriodState) {
  return useQuery({
    enabled: managerId !== null,
    queryKey: ['dashboard', 'admin', 'manager', managerId, period.key, period.from, period.to],
    queryFn: async () => {
      const res = await apiClient.get<{ success: true; data: ManagerDetailDto }>(
        `/dashboard/admin/manager/${managerId}`,
        { params: periodParams(period) },
      )
      return res.data.data
    },
  })
}

export function useProcurementDetail(userId: string | null, period: DashboardPeriodState) {
  return useQuery({
    enabled: userId !== null,
    queryKey: ['dashboard', 'admin', 'procurement', userId, period.key, period.from, period.to],
    queryFn: async () => {
      const res = await apiClient.get<{ success: true; data: ProcurementDetailDto }>(
        `/dashboard/admin/procurement/${userId}`,
        { params: periodParams(period) },
      )
      return res.data.data
    },
  })
}

export function usePartPriceHistory(target: { partNumber: string; brand: string | null } | null) {
  return useQuery({
    enabled: target !== null,
    queryKey: ['dashboard', 'admin', 'part-history', target?.partNumber, target?.brand],
    queryFn: async () => {
      const res = await apiClient.get<{ success: true; data: PartPriceHistoryDto }>(
        '/dashboard/admin/part-history',
        { params: { partNumber: target!.partNumber, brand: target!.brand ?? undefined } },
      )
      return res.data.data
    },
  })
}

export function useManagersMonthly(enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['dashboard', 'admin', 'managers-monthly'],
    queryFn: async () => {
      const res = await apiClient.get<{ success: true; data: ManagersMonthlyDto }>(
        '/dashboard/admin/managers-monthly',
      )
      return res.data.data
    },
    staleTime: 60_000,
  })
}

export function useClientDetail(clientId: string | null) {
  return useQuery({
    enabled: clientId !== null,
    queryKey: ['dashboard', 'admin', 'client', clientId],
    queryFn: async () => {
      const res = await apiClient.get<{ success: true; data: ClientDetailDto }>(
        `/dashboard/admin/client/${clientId}`,
      )
      return res.data.data
    },
  })
}
